import { createServerFn } from "@tanstack/react-start";

export const checkEmailExists = createServerFn({ method: "POST" })
  .inputValidator((input: { email: string }) => {
    const email = String(input?.email ?? "").trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error("Valid email is required");
    }
    if (email.length > 255) throw new Error("Email too long");
    return { email };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // profiles.email is kept in sync with auth.users by a DB trigger, so a
    // missing profile effectively means no account exists for this address.
    const { data: profile, error } = await supabaseAdmin
      .from("profiles")
      .select("id")
      .ilike("email", data.email)
      .maybeSingle();

    if (error) throw new Error(error.message);
    return { exists: !!profile };
  });

/**
 * True when this email belonged to an account deleted within the last 24h.
 * Used on the sign-in page so a deleted account cannot "sign in normally" —
 * the user is told the account was deleted and must sign up again.
 */
export const checkEmailDeleted = createServerFn({ method: "POST" })
  .inputValidator((input: { email: string }) => {
    const email = String(input?.email ?? "").trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error("Valid email is required");
    }
    return { email };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: deleted, error } = await supabaseAdmin
      .rpc("is_recently_deleted_email", { _email: data.email });
    if (error) throw new Error(error.message);
    return { deleted: !!deleted };
  });
